import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import {
  useValueLookups,
  ValueLookupValueType,
} from 'features/libra/stepComponents/conditionalForm/api/conditionalFormApi'
import { useRolodexEntries } from 'features/rolodex'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'
import { createContext, FC, ReactNode, useContext, useMemo } from 'react'
import { RolodexEntryType } from 'types'

const ROLODEX_DOC_FIELDS_TYPE = RolodexEntryType.DOCUMENT_FIELDS

interface FieldValueLookupContextType {
  fieldToValues: ReadonlyMap<string, ReadonlyArray<ValueLookupValueType>>
  isLoading: boolean
}

const FieldValueLookupContext = createContext<FieldValueLookupContextType>({
  fieldToValues: new Map(),
  isLoading: false,
})

/**
 * A context to hold the conditional form value lookups for the ticket currently in scope.
 * Automatically includes values from document-fields entries.
 */
function useCreateContext(
  config: Parameters<typeof useValueLookups>[0] | undefined
): FieldValueLookupContextType {
  const { values, isLoading: configLoading } = useValueLookups(config)
  const { unitId } = useLibraDocument()

  // Fetch all document-fields entries for this unit
  const { entries: docFieldEntries, isLoading: docFieldsLoading } =
    useRolodexEntries(
      useMemo(
        () =>
          unitId
            ? {
                type: ROLODEX_DOC_FIELDS_TYPE,
                tags: { unit: unitId },
                links: [RolodexEntryType.REPORT],
              }
            : undefined,
        [unitId]
      ),
      { swrOptions: { revalidateOnFocus: false, compare: isEqual } }
    )

  // Convert document-fields entries to ValueLookupValueType format
  const docFieldValues = useMemo(() => {
    const allValues: ValueLookupValueType[] = []

    // Separate document-fields entries from report entries
    const docFieldsEntries = docFieldEntries.filter(
      (entry) => entry.type === ROLODEX_DOC_FIELDS_TYPE
    )
    const reportEntries = docFieldEntries.filter(
      (entry) => entry.type === RolodexEntryType.REPORT
    )
    const reportEntriesMap = new Map(
      reportEntries.map((entry) => [entry.id, entry])
    )

    docFieldsEntries.forEach((entry) => {
      const fields = entry.data?.fields as Record<string, unknown> | undefined
      if (!fields) return

      // Extract document ID from tags
      const documentId = entry.tags?.[RolodexEntryType.REPORT]

      // Find the linked report entry
      const reportEntry = documentId
        ? reportEntriesMap.get(documentId)
        : undefined

      // Create a lookup value for each field in the document-fields entry
      Object.entries(fields).forEach(([fieldKey, fieldValue]) => {
        if (fieldValue !== undefined && documentId && reportEntry) {
          const lookupValue: ValueLookupValueType = {
            field: fieldKey,
            sourceName: (entry.data?.title as string) || 'Document',
            sourceId: entry.id,
            value: fieldValue as ValueLookupValueType['value'],
            nameEntry: reportEntry,
          }
          allValues.push(lookupValue)
        }
      })
    })

    return allValues
  }, [docFieldEntries])

  // Combine configured values with document-fields values
  const combinedValues = useMemo(() => {
    return [...(values ?? []), ...docFieldValues]
  }, [values, docFieldValues])

  const fieldToValues = useMemo(() => {
    return new Map(
      Object.entries(groupBy(combinedValues, 'field')).map(([field, vals]) => [
        field,
        sortBy(vals, 'name'),
      ])
    ) as ReadonlyMap<string, ReadonlyArray<ValueLookupValueType>>
  }, [combinedValues])

  return {
    fieldToValues,
    isLoading: configLoading || docFieldsLoading,
  }
}

export function useFieldValueLookup(field: string): {
  values: ReadonlyArray<ValueLookupValueType>
  isLoading: boolean
} {
  const { fieldToValues, isLoading } = useContext(FieldValueLookupContext)
  return useMemo(
    () => ({
      values: fieldToValues.get(field) ?? [],
      isLoading,
    }),
    [fieldToValues, isLoading, field]
  )
}

export const FieldValueLookupProvider: FC<{
  children: ReactNode
  config: Parameters<typeof useValueLookups>[0] | undefined
}> = ({ children, config }) => {
  const context = useCreateContext(config)
  return (
    <FieldValueLookupContext.Provider value={context}>
      {children}
    </FieldValueLookupContext.Provider>
  )
}
